import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CampaignOperation, LiveCall, OperationsStatus } from '../models/types';
import { ApiService } from '../services/api.service';
import { formatDuration } from '../utils/duration';
import { completionLabel } from '../utils/call-completion';

@Component({
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="page">
      <div class="top">
        <div>
          <p class="ux-caption">Workspace overview</p>
          <h1>Keep every conversation moving</h1>
          <p>Monitor live calls, check agent readiness, and review what needs attention.</p>
        </div>
        <button class="refresh" (click)="load()" [disabled]="loading()">
          {{ loading() ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>

      <div class="ux-toolbar">
        <a class="ux-link-button" routerLink="/test-call">Make a test call ↗</a
        ><a class="ux-link-button" routerLink="/campaigns">Manage agents</a
        ><a class="ux-link-button" routerLink="/leads">View leads & outcomes</a
        ><a class="ux-link-button" routerLink="/call-history">Review calls & recordings</a>
      </div>
      <div class="ux-summary" *ngIf="status() as data">
        <div>
          <span>Live conversations</span><strong>{{ allLive().length }}</strong>
        </div>
        <div>
          <span>Configured agents</span><strong>{{ data.campaigns.length }}</strong>
        </div>
        <div>
          <span>Calling service</span
          ><strong style="font-size:20px">{{ data.dialer.online ? 'Online' : 'Offline' }}</strong>
        </div>
      </div>
      <div class="error" role="alert" *ngIf="error()">{{ error() }}</div>

      <section class="dialer" *ngIf="status() as data">
        <span
          class="dot"
          [class.online]="data.dialer.online"
          [class.dry]="data.dialer.mode === 'dry'"
        ></span>
        <div>
          <strong>Calling service: {{ data.dialer.mode }}</strong>
          <p>{{ data.dialer.message }}</p>
        </div>
        <small>Updated {{ data.refreshed_at | date: 'mediumTime' }}</small>
      </section>

      <section class="live-board" *ngIf="allLive().length">
        <h2>Live now ({{ allLive().length }})</h2>
        <div class="live-card" *ngFor="let call of allLive()">
          <div class="live-head">
            <div>
              <strong>{{ call.lead_name || 'Unknown' }}</strong>
              <span>{{ call.phone || '—' }}</span>
            </div>
            <span class="phase" [attr.data-phase]="call.phase">{{ call.talking }}</span>
          </div>
          <p class="meta">
            {{ call.campaign_name || 'Campaign' }} · {{ call.direction }} ·
            {{ formatDuration(call.duration_seconds) }}
          </p>
          <p class="snippet" *ngIf="call.last_text">
            <em>{{ call.last_speaker || '…' }}:</em> {{ call.last_text }}
          </p>
          <p class="snippet muted" *ngIf="!call.last_text && call.detail">{{ call.detail }}</p>
        </div>
      </section>

      <div class="empty" *ngIf="!loading() && status()?.campaigns?.length === 0">
        No campaigns yet. Create one from the Campaigns page.
      </div>

      <section class="campaign" *ngFor="let campaign of status()?.campaigns">
        <div class="campaign-head">
          <div>
            <div class="title-line">
              <a [routerLink]="['/campaigns', campaign.campaign_id]">{{ campaign.name }}</a>
              <span class="state" [attr.data-state]="campaign.execution_state">
                {{ label(campaign.execution_state) }}
              </span>
            </div>
            <p>
              {{ campaign.campaign_type }} · configured {{ campaign.configured_status }} ·
              {{ campaign.window }} {{ campaign.timezone }}
            </p>
          </div>
          <strong class="percent" *ngIf="campaign.campaign_type === 'outbound'"
            >{{ campaign.completed_percent }}%</strong
          >
        </div>

        <div class="progress" *ngIf="campaign.campaign_type === 'outbound'">
          <span [style.width.%]="campaign.completed_percent"></span>
        </div>

        <div class="metrics">
          <div *ngIf="campaign.campaign_type === 'outbound'">
            <strong>{{ campaign.total_leads }}</strong
            ><span>Leads</span>
          </div>
          <div class="metric-pending" *ngIf="campaign.campaign_type === 'outbound'">
            <strong>{{ pending(campaign) }}</strong
            ><span>Pending</span>
          </div>
          <div class="metric-live" [class.active]="campaign.running_calls > 0">
            <strong>{{ campaign.running_calls }}</strong
            ><span>Live calls</span>
          </div>
          <div>
            <strong>{{ campaign.total_calls }}</strong
            ><span>Total calls</span>
          </div>
          <div class="metric-forms" [class.warn]="campaign.failed_forms > 0">
            <strong>{{ campaign.failed_forms }}</strong
            ><span>Form issues</span>
          </div>
        </div>

        <div class="disp" *ngIf="dispositionEntries(campaign).length">
          <h3>Call outcomes</h3>
          <div class="chips">
            <span
              class="chip"
              *ngFor="let item of dispositionEntries(campaign)"
              [attr.data-disp]="item.id"
            >
              {{ item.label }} <strong>{{ item.count }}</strong>
            </span>
          </div>
        </div>

        <div class="blockers" *ngIf="campaign.blockers.length">
          <strong>Needs attention</strong>
          <ul>
            <li *ngFor="let blocker of campaign.blockers">{{ blocker }}</li>
          </ul>
        </div>

        <div class="live-now" *ngIf="campaign.live_calls.length">
          <h3>
            <span class="pulse" aria-hidden="true"></span>
            On this campaign now
          </h3>
          <div
            class="live-item"
            *ngFor="let call of campaign.live_calls"
            [attr.data-phase]="call.phase"
          >
            <div class="live-item-head">
              <div>
                <strong>{{ call.lead_name || 'Unknown' }}</strong>
                <span class="phone">{{ call.phone || '—' }}</span>
              </div>
              <div class="live-item-aside">
                <span class="phase" [attr.data-phase]="call.phase">{{ call.talking }}</span>
                <span class="duration">{{ formatDuration(call.duration_seconds) }}</span>
              </div>
            </div>
            <p class="direction">
              {{ call.direction }} · {{ call.campaign_name || campaign.name }}
            </p>
            <p class="snippet" *ngIf="call.last_text">
              <em>{{ call.last_speaker || '…' }}:</em> {{ call.last_text }}
            </p>
            <p class="snippet muted" *ngIf="!call.last_text && call.detail">{{ call.detail }}</p>
          </div>
        </div>

        <div class="recent" *ngIf="campaign.recent_calls.length">
          <h3>Recent calls</h3>
          <div
            class="history-item"
            *ngFor="let call of campaign.recent_calls"
            [attr.data-status]="normalizeStatus(call.status)"
          >
            <div class="history-head">
              <div>
                <strong>{{ call.lead_name || call.phone || 'Unknown number' }}</strong>
                <span>{{ call.phone }} · {{ call.direction }}</span>
              </div>
              <div class="history-aside">
                <span class="status-pill" [attr.data-status]="normalizeStatus(call.status)">
                  {{ completionLabel(call.completion_state) }}
                </span>
                <span class="duration">{{ formatDuration(call.duration_seconds) }}</span>
                <small *ngIf="call.cost_pkr">Rs {{ call.cost_pkr | number: '1.0-2' }}</small>
                <small>{{ call.started_at | date: 'short' }}</small>
              </div>
            </div>
            <p *ngIf="call.completion_reason">{{ call.completion_reason }}</p>
            <p *ngIf="call.retry_at">Retry eligible: {{ call.retry_at | date: 'medium' }}</p>
            <p class="summary" *ngIf="call.summary">{{ call.summary }}</p>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [
    `
      .page {
        display: grid;
        gap: 16px;
      }
      .top,
      .campaign-head,
      .title-line,
      .dialer,
      .live-head {
        display: flex;
        align-items: center;
      }
      .top,
      .campaign-head,
      .live-head {
        justify-content: space-between;
        gap: 16px;
      }
      h1,
      h2,
      h3,
      p {
        margin: 0;
      }
      h1,
      h2 {
        font-family: Georgia, serif;
      }
      p,
      small {
        color: #64748b;
      }
      .refresh {
        border: 0;
        border-radius: 10px;
        padding: 10px 14px;
        background: #0f766e;
        color: white;
        cursor: pointer;
      }
      .refresh:disabled {
        opacity: 0.6;
      }
      .dialer,
      .campaign,
      .empty,
      .error,
      .live-board {
        background: white;
        border-radius: 16px;
        padding: 16px;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
      }
      .dialer {
        gap: 12px;
      }
      .dialer small {
        margin-left: auto;
      }
      .dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #ef4444;
        box-shadow: 0 0 0 4px #fee2e2;
      }
      .dot.online {
        background: #22c55e;
        box-shadow: 0 0 0 4px #dcfce7;
      }
      .dot.dry {
        background: #f59e0b;
        box-shadow: 0 0 0 4px #fef3c7;
      }
      .live-board {
        display: grid;
        gap: 12px;
        border: 1px solid #99f6e4;
        background: linear-gradient(180deg, #f0fdfa, #ffffff);
      }
      .live-card {
        border: 1px solid #ccfbf1;
        border-radius: 12px;
        padding: 12px;
        background: white;
      }
      .live-head strong {
        display: block;
        font-size: 16px;
      }
      .live-head span {
        color: #64748b;
        font-size: 13px;
      }
      .phase {
        border-radius: 999px;
        padding: 4px 10px;
        background: #ecfdf5;
        color: #0f766e;
        font-size: 12px;
        font-weight: 700;
      }
      .phase[data-phase='ai_speaking'] {
        background: #dbeafe;
        color: #1d4ed8;
      }
      .phase[data-phase='caller_speaking'] {
        background: #fef3c7;
        color: #92400e;
      }
      .phase[data-phase='ringing'],
      .phase[data-phase='dialing'] {
        background: #e2e8f0;
        color: #334155;
      }
      .snippet {
        margin-top: 8px;
        font-size: 13px;
        color: #0f172a;
      }
      .snippet.muted,
      .meta {
        color: #64748b;
        font-size: 12px;
        margin-top: 4px;
      }
      .campaign {
        display: grid;
        gap: 14px;
      }
      .title-line {
        gap: 10px;
      }
      .title-line a {
        color: #0f172a;
        font-size: 18px;
        font-weight: 700;
      }
      .state {
        border-radius: 999px;
        padding: 4px 8px;
        background: #e2e8f0;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
      }
      .state[data-state='running'],
      .state[data-state='ready'] {
        color: #166534;
        background: #dcfce7;
      }
      .state[data-state='attention'],
      .state[data-state='blocked'] {
        color: #92400e;
        background: #fef3c7;
      }
      .percent {
        font-size: 24px;
        color: #0f766e;
      }
      .progress {
        height: 8px;
        overflow: hidden;
        border-radius: 999px;
        background: #e2e8f0;
      }
      .progress span {
        display: block;
        height: 100%;
        background: #14b8a6;
      }
      .metrics {
        display: grid;
        grid-template-columns: repeat(5, minmax(90px, 1fr));
        gap: 10px;
      }
      .metrics div {
        display: grid;
        gap: 2px;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 10px;
        background: #f8fafc;
      }
      .metrics strong {
        font-size: 20px;
      }
      .metrics span {
        color: #64748b;
        font-size: 12px;
      }
      .metric-live.active {
        border-color: #5eead4;
        background: #f0fdfa;
      }
      .metric-live.active strong {
        color: #0f766e;
      }
      .metric-pending {
        border-color: #fde68a;
        background: #fffbeb;
      }
      .metric-forms.warn {
        border-color: #fca5a5;
        background: #fef2f2;
      }
      .metric-forms.warn strong {
        color: #b91c1c;
      }
      .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 8px;
      }
      .chip {
        border-radius: 999px;
        padding: 4px 10px;
        background: #f1f5f9;
        color: #334155;
        font-size: 12px;
        border: 1px solid #e2e8f0;
      }
      .chip[data-disp='converted'],
      .chip[data-disp='interested'] {
        background: #dcfce7;
        border-color: #86efac;
        color: #166534;
      }
      .chip[data-disp='callback'] {
        background: #dbeafe;
        border-color: #93c5fd;
        color: #1d4ed8;
      }
      .chip[data-disp='not_interested'],
      .chip[data-disp='do_not_call'],
      .chip[data-disp='wrong_number'] {
        background: #fee2e2;
        border-color: #fca5a5;
        color: #991b1b;
      }
      .chip[data-disp='incomplete'],
      .chip[data-disp='no_answer'],
      .chip[data-disp='busy'],
      .chip[data-disp='no_response'],
      .chip[data-disp='voicemail'] {
        background: #ffedd5;
        border-color: #fdba74;
        color: #9a3412;
      }
      .blockers {
        border-left: 4px solid #f59e0b;
        border-radius: 8px;
        padding: 10px 12px;
        background: #fffbeb;
      }
      .blockers ul {
        margin: 6px 0 0;
        padding-left: 20px;
      }
      .recent h3,
      .disp h3,
      .live-now h3 {
        margin-bottom: 10px;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .live-now {
        display: grid;
        gap: 10px;
        padding: 12px;
        border-radius: 14px;
        border: 1px solid #99f6e4;
        background: linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%);
      }
      .pulse {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #14b8a6;
        box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.55);
        animation: pulse 1.6s ease-out infinite;
      }
      @keyframes pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.55);
        }
        70% {
          box-shadow: 0 0 0 8px rgba(20, 184, 166, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(20, 184, 166, 0);
        }
      }
      .live-item {
        display: grid;
        gap: 6px;
        padding: 12px;
        border-radius: 12px;
        border: 1px solid #99f6e4;
        background: #ffffff;
        border-left: 4px solid #14b8a6;
      }
      .live-item[data-phase='ai_speaking'] {
        border-left-color: #3b82f6;
        border-color: #bfdbfe;
        background: #f8fbff;
      }
      .live-item[data-phase='caller_speaking'] {
        border-left-color: #d97706;
        border-color: #fde68a;
        background: #fffbeb;
      }
      .live-item[data-phase='ringing'],
      .live-item[data-phase='dialing'] {
        border-left-color: #64748b;
        border-color: #cbd5e1;
        background: #f8fafc;
      }
      .live-item[data-phase='connected'],
      .live-item[data-phase='waiting'] {
        border-left-color: #14b8a6;
      }
      .live-item-head,
      .history-head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
      }
      .live-item-head strong,
      .history-head strong {
        display: block;
        font-size: 15px;
      }
      .phone,
      .live-item-head .phone,
      .history-head span,
      .direction {
        color: #64748b;
        font-size: 12px;
      }
      .live-item-aside,
      .history-aside {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
      }
      .duration {
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        color: #0f172a;
        font-size: 13px;
      }
      .history-item {
        display: grid;
        gap: 6px;
        padding: 12px;
        border-radius: 12px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        margin-top: 8px;
        border-left: 4px solid #94a3b8;
      }
      .history-item[data-status='completed'],
      .history-item[data-status='converted'],
      .history-item[data-status='interested'] {
        border-left-color: #22c55e;
        background: #f0fdf4;
        border-color: #bbf7d0;
      }
      .history-item[data-status='failed'],
      .history-item[data-status='do_not_call'],
      .history-item[data-status='not_interested'],
      .history-item[data-status='wrong_number'] {
        border-left-color: #ef4444;
        background: #fef2f2;
        border-color: #fecaca;
      }
      .history-item[data-status='started'],
      .history-item[data-status='calling'],
      .history-item[data-status='ringing'] {
        border-left-color: #3b82f6;
        background: #eff6ff;
        border-color: #bfdbfe;
      }
      .history-item[data-status='no_answer'],
      .history-item[data-status='busy'],
      .history-item[data-status='machine'],
      .history-item[data-status='voicemail'],
      .history-item[data-status='incomplete'],
      .history-item[data-status='no_response'] {
        border-left-color: #f59e0b;
        background: #fffbeb;
        border-color: #fde68a;
      }
      .history-item[data-status='scheduled'],
      .history-item[data-status='callback'],
      .history-item[data-status='customer_asked_later'] {
        border-left-color: #6366f1;
        background: #eef2ff;
        border-color: #c7d2fe;
      }
      .status-pill {
        border-radius: 999px;
        padding: 3px 9px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        background: #e2e8f0;
        color: #334155;
      }
      .status-pill[data-status='completed'],
      .status-pill[data-status='converted'],
      .status-pill[data-status='interested'] {
        background: #dcfce7;
        color: #166534;
      }
      .status-pill[data-status='failed'],
      .status-pill[data-status='do_not_call'],
      .status-pill[data-status='not_interested'],
      .status-pill[data-status='wrong_number'] {
        background: #fee2e2;
        color: #991b1b;
      }
      .status-pill[data-status='started'],
      .status-pill[data-status='calling'],
      .status-pill[data-status='ringing'] {
        background: #dbeafe;
        color: #1d4ed8;
      }
      .status-pill[data-status='no_answer'],
      .status-pill[data-status='busy'],
      .status-pill[data-status='machine'],
      .status-pill[data-status='voicemail'],
      .status-pill[data-status='incomplete'],
      .status-pill[data-status='no_response'] {
        background: #ffedd5;
        color: #9a3412;
      }
      .status-pill[data-status='scheduled'],
      .status-pill[data-status='callback'],
      .status-pill[data-status='customer_asked_later'] {
        background: #e0e7ff;
        color: #3730a3;
      }
      .summary {
        margin: 0;
        font-size: 13px;
        color: #475569;
      }
      .error {
        color: #b91c1c;
        background: #fef2f2;
      }
      @media (max-width: 800px) {
        .metrics {
          grid-template-columns: repeat(2, 1fr);
        }
        .dialer {
          align-items: flex-start;
        }
        .dialer small {
          display: none;
        }
        .live-item-head,
        .history-head {
          flex-direction: column;
        }
        .live-item-aside,
        .history-aside {
          justify-content: flex-start;
        }
      }
    `,
  ],
})
export class OperationsPage implements OnInit, OnDestroy {
  status = signal<OperationsStatus | null>(null);
  loading = signal(false);
  error = signal('');
  private refreshTimer?: ReturnType<typeof setInterval>;

  constructor(private readonly api: ApiService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
    this.refreshTimer = setInterval(() => void this.load(), 5_000);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  async load(): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    this.error.set('');
    try {
      this.status.set(await this.api.operations());
    } catch (error: any) {
      this.error.set(error?.error?.detail || 'Could not load operations status');
    } finally {
      this.loading.set(false);
    }
  }

  allLive(): LiveCall[] {
    const data = this.status();
    if (!data) return [];
    if (data.live_calls?.length) return data.live_calls;
    return data.campaigns.flatMap((c) => c.live_calls || []);
  }

  pending(campaign: CampaignOperation): number {
    return (campaign.lead_counts['pending'] || 0) + (campaign.lead_counts['scheduled'] || 0);
  }

  dispositionEntries(
    campaign: CampaignOperation,
  ): Array<{ id: string; label: string; count: number }> {
    const counts = campaign.disposition_counts || {};
    const labels = campaign.disposition_labels || {};
    return Object.entries(counts)
      .filter(([, count]) => count > 0)
      .map(([id, count]) => ({
        id,
        label: labels[id] || id.replaceAll('_', ' '),
        count,
      }));
  }

  label(value: string): string {
    return (value || '').replaceAll('_', ' ');
  }

  normalizeStatus(value: string | null | undefined): string {
    return (value || 'unknown').trim().toLowerCase().replace(/\s+/g, '_');
  }

  formatDuration = formatDuration;
  completionLabel = completionLabel;
}
